import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Trophy, Flame, Pen, MessageCircle, Bot, Users, Zap, Palette, Heart, Lock, Star, Home, Search, Plus, Bell, User } from "lucide-react";
import api from "../lib/api";

const ICON_MAP = { fire: Flame, pen: Pen, chat: MessageCircle, bot: Bot, users: Users, flame: Flame, lightning: Zap, palette: Palette, heart: Heart, trophy: Trophy, lock: Lock, star: Star };
const COLOR_MAP = { posting: "text-orange-500 bg-orange-500/10", social: "text-emerald-500 bg-emerald-500/10", ai: "text-violet-500 bg-violet-500/10", streak: "text-red-500 bg-red-500/10", special: "text-cyan-400 bg-cyan-500/10" };

export default function AchievementsPage() {
  const navigate = useNavigate();
  const [allAchievements, setAllAchievements] = useState([]);
  const [earned, setEarned] = useState([]);
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    api.get("/profile/me").then((r) => setProfile(r.data)).catch(() => {});
    Promise.all([api.get("/achievements"), api.get("/achievements/me"), api.get("/streaks/me")])
      .then(([a, e, s]) => { setAllAchievements(a.data); setEarned(e.data); setStreak(s.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const earnedNames = new Set(earned.map((e) => e.achievement_name));
  const earnedMap = Object.fromEntries(earned.map((e) => [e.achievement_name, e.earned_at]));

  return (
    <div className="min-h-screen pb-20 lg:pb-0" data-testid="achievements-page">
      <div className="sticky top-0 z-30 nav-glass px-4 h-12 flex items-center gap-3">
        <button onClick={() => navigate("/feed")} className="text-muted-foreground hover:text-foreground/80 bg-transparent border-0 cursor-pointer"><ArrowLeft className="w-5 h-5" /></button>
        <Trophy className="w-4 h-4 text-amber-500" />
        <h1 className="font-outfit text-base font-semibold text-foreground">Achievements</h1>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
        {/* Hero Image */}
        <div className="rounded-xl overflow-hidden h-32 relative" data-testid="achievements-hero">
          <img src="https://static.prod-images.emergentagent.com/jobs/69522626-12bf-4c16-9d63-ce7a21ed458c/images/fd25fe774780ef8fd1fd1678516c508bea3898b765da7b04d5c8053e15b79ba8.jpeg" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-amber-600/60 to-orange-600/40" />
          <div className="absolute inset-0 flex items-center justify-center">
            <h2 className="font-outfit text-xl font-semibold text-white drop-shadow-lg">Your Achievement Collection</h2>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-cyan-400 animate-spin" /></div>
        ) : (
          <>
            {/* Streak Card */}
            {streak && (
              <div className="bg-gradient-to-r from-orange-500 to-red-500 rounded-xl p-5 flex items-center gap-4" data-testid="streak-card">
                <div className="text-3xl">{streak.current_streak > 0 ? "🔥" : "💤"}</div>
                <div>
                  <p className="text-lg font-semibold text-white font-outfit">{streak.current_streak} day streak</p>
                  <p className="text-xs text-orange-100">Best: {streak.longest_streak} days &middot; Total posts: {streak.total_posts}</p>
                  {!streak.posted_today && <p className="text-xs text-orange-200 font-medium mt-1">Post today to keep your streak alive!</p>}
                </div>
              </div>
            )}

            {/* Stats */}
            <div className="flex gap-4">
              <div className="flex-1 bg-card border border-border/60 rounded-xl p-4 text-center">
                <p className="text-xl font-semibold text-cyan-400 font-outfit">{earned.length}</p>
                <p className="text-xs text-muted-foreground">Earned</p>
              </div>
              <div className="flex-1 bg-card border border-border/60 rounded-xl p-4 text-center">
                <p className="text-xl font-semibold text-muted-foreground/70 font-outfit">{allAchievements.length - earned.length}</p>
                <p className="text-xs text-muted-foreground">Locked</p>
              </div>
            </div>

            {/* Achievement Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="achievements-grid">
              {allAchievements.map((ach) => {
                const isEarned = earnedNames.has(ach.name);
                const Icon = ICON_MAP[ach.icon] || Star;
                const color = COLOR_MAP[ach.category] || COLOR_MAP.special;
                return (
                  <div
                    key={ach.name}
                    className={`bg-card border rounded-xl p-4 text-center transition-all ${
                      isEarned ? "border-amber-500/30 shadow-[0_0_15px_-3px_rgba(245,158,11,0.3)]" : "border-border opacity-40 grayscale"
                    }`}
                    data-testid={`achievement-${ach.name.toLowerCase().replace(/ /g, "-")}`}
                  >
                    <div className={`w-10 h-10 mx-auto rounded-xl flex items-center justify-center mb-2 ${isEarned ? color : "bg-muted/70 text-muted-foreground/70"}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <p className="text-xs font-semibold text-foreground mb-0.5">{ach.name}</p>
                    <p className="text-[10px] text-muted-foreground">{ach.description}</p>
                    {isEarned && (
                      <p className="text-[9px] text-amber-400 font-medium mt-1">
                        Earned {new Date(earnedMap[ach.name]).toLocaleDateString([], { month: "short", day: "numeric" })}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Mobile Bottom Tabs */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-card border-t border-border/60 h-14 flex items-center justify-around px-2">
        <Link to="/feed" className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><Home className="w-5 h-5" /><span className="text-[10px]">Home</span></Link>
        <Link to="/explore" className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><Search className="w-5 h-5" /><span className="text-[10px]">Explore</span></Link>
        <Link to="/feed" className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><Plus className="w-5 h-5" /></Link>
        <Link to="/notifications" className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><Bell className="w-5 h-5" /><span className="text-[10px]">Notifs</span></Link>
        <Link to={profile ? `/profile/${profile.username}` : "/feed"} className="flex flex-col items-center gap-0.5 no-underline text-muted-foreground/70"><User className="w-5 h-5" /><span className="text-[10px]">Profile</span></Link>
      </div>
    </div>
  );
}
